import { Card, Group, Stack, Text, ThemeIcon, Title } from '@mantine/core';
import {
  IconArrowsExchange,
  IconChevronRight,
  IconCoin,
  IconPackage,
  IconRuler2,
  IconShip,
  IconTruckDelivery,
  IconWorld,
} from '@tabler/icons-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

/** Один справочник НСИ в списке раздела. */
interface ReferenceBook {
  to: string;
  title: string;
  description: string;
  icon: ReactNode;
}

/**
 * Список справочников НСИ. Раздел расширяемый — новые справочники добавляются
 * сюда одной записью (+ своя страница и маршрут).
 */
const BOOKS: ReferenceBook[] = [
  {
    to: '/manager/reference-data/gng',
    title: 'ГНГ — Гармонизированная номенклатура грузов',
    description:
      'Коды ГНГ (8 знаков, ОСЖД/СМГС) с наименованиями и классом ЕТТ. Используется в форме поручения; импорт официального файла ОСЖД.',
    icon: <IconArrowsExchange size={22} />,
  },
  {
    to: '/manager/reference-data/etsng',
    title: 'ЕТСНГ — Единая тарифно-статистическая номенклатура грузов',
    description:
      'Коды ЕТСНГ (6 знаков, РЖД) с наименованиями, тарифным классом и признаком опасности. Импорт справочника РЖД.',
    icon: <IconArrowsExchange size={22} />,
  },
  {
    to: '/manager/reference-data/link',
    title: 'Переходник ГНГ ↔ ЕТСНГ',
    description:
      'Официальные перекодировочные таблицы Совета по ж/д транспорту. В поручении по одному коду подставляется/предлагается встречный.',
    icon: <IconArrowsExchange size={22} />,
  },
  {
    to: '/manager/reference-data/classifier/country',
    title: 'Страны (ФТС)',
    description: 'Классификатор стран (alpha-2, ОКСМ). Используется в стране происхождения груза.',
    icon: <IconWorld size={22} />,
  },
  {
    to: '/manager/reference-data/classifier/packaging',
    title: 'Виды упаковки (ФТС)',
    description: 'Классификатор видов упаковки (код + рус/англ наименование).',
    icon: <IconPackage size={22} />,
  },
  {
    to: '/manager/reference-data/classifier/delivery-terms',
    title: 'Условия поставки (Incoterms)',
    description: 'Классификатор условий поставки ФТС (Incoterms).',
    icon: <IconTruckDelivery size={22} />,
  },
  {
    to: '/manager/reference-data/classifier/currency',
    title: 'Валюты (ФТС)',
    description: 'Классификатор валют (ISO 4217). Используется в поле «Валюта» стоимости груза.',
    icon: <IconCoin size={22} />,
  },
  {
    to: '/manager/reference-data/classifier/transport',
    title: 'Виды транспорта (ФТС)',
    description: 'Классификатор видов транспорта. Для транзитной декларации.',
    icon: <IconShip size={22} />,
  },
  {
    to: '/manager/reference-data/classifier/units',
    title: 'Единицы измерения (ФТС)',
    description: 'Классификатор единиц измерения (код + условное обозначение).',
    icon: <IconRuler2 size={22} />,
  },
];

/** Админ: раздел «Справочники» (нормативно-справочная информация). */
export function ReferenceDataPage() {
  return (
    <Stack>
      <Title order={2}>Справочники</Title>
      <Text c="dimmed" size="sm">
        Нормативно-справочная информация (НСИ), используемая в поручениях и документах. Выберите
        справочник для просмотра и настройки.
      </Text>

      <Stack gap="sm">
        {BOOKS.map((b) => (
          <Card
            key={b.to}
            component={Link}
            to={b.to}
            withBorder
            padding="md"
            style={{ cursor: 'pointer' }}
          >
            <Group justify="space-between" wrap="nowrap">
              <Group wrap="nowrap" gap="md">
                <ThemeIcon variant="light" size={44} radius="md">
                  {b.icon}
                </ThemeIcon>
                <Stack gap={2}>
                  <Text fw={500}>{b.title}</Text>
                  <Text size="sm" c="dimmed">
                    {b.description}
                  </Text>
                </Stack>
              </Group>
              <IconChevronRight size={18} style={{ flexShrink: 0, opacity: 0.5 }} />
            </Group>
          </Card>
        ))}
      </Stack>
    </Stack>
  );
}
